import { Applicant } from "@/shared/core-vouchers";
import {
  useSimilarRecords,
  type SimilarReason,
} from "@/core/common/hooks/useSimilarRecords";
import {
  isSimilarName,
  normalizePhone,
  normalizeText,
  searchTermsFor,
} from "@/core/common/utils/similarity";
import type { SimilarRecordItem } from "@/ui/SimilarRecordsHint";
import { getApplicants } from "./useGetApplicants";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SimilarApplicantsInput {
  name?: string;
  email?: string;
  phone?: string;
  zoneId?: string;
  excludeId?: string;
}

export const useSimilarApplicants = ({
  name = "",
  email = "",
  phone = "",
  zoneId,
  excludeId,
}: SimilarApplicantsInput): SimilarRecordItem[] => {
  const normalizedEmail = normalizeText(email);
  const hasEmail = EMAIL_PATTERN.test(normalizedEmail);
  const normalizedPhone = normalizePhone(phone);

  const { matches } = useSimilarRecords<Applicant>({
    key: `applicants:${zoneId}`,
    terms: [...searchTermsFor(name), ...(hasEmail ? [normalizedEmail] : [])],
    fetchCandidates: async (term) =>
      (await getApplicants(zoneId, true, 1, 20, term)).data,
    getId: (applicant) => applicant.id,
    match: (applicant) => {
      if (applicant.id === excludeId) return [];
      const reasons: SimilarReason[] = [];
      if (hasEmail && normalizeText(applicant.email ?? "") === normalizedEmail)
        reasons.push("email");
      if (
        normalizedPhone.length === 8 &&
        normalizePhone(applicant.phone ?? "") === normalizedPhone
      )
        reasons.push("phone");
      if (isSimilarName(name, applicant.name)) reasons.push("name");
      return reasons;
    },
    enabled: !!zoneId,
  });

  return matches.map(({ item, reasons }) => ({
    id: item.id,
    title: item.name,
    subtitle: [item.email, item.company?.name].filter(Boolean).join(" · "),
    reasons,
    enabled: item.enabled,
  }));
};
